import React from 'react';
import { Store, Building2, MapPin, CreditCard, GitBranch, CornerDownRight, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { roleLabel } from './attributionApi';

const INDENT = ['pl-0', 'pl-2.5 sm:pl-5', 'pl-5 sm:pl-10', 'pl-7 sm:pl-[60px]'];

const ICONS = { merchant_group: Store, legal_entity: Building2, location: MapPin, processing_account: CreditCard };

function Badge({ node }) {
  if (!node.attribution_id) return <span className="text-xs text-gray-400" data-testid={`attr-badge-${node.scope_id}`}>Unattributed</span>;
  if (node.inherited) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-testid={`attr-badge-${node.scope_id}`}>
        <CornerDownRight className="w-3 h-3" /> inherits from {roleLabel(node.resolved_scope)}
      </span>
    );
  }
  return (
    <span data-testid={`attr-badge-${node.scope_id}`}
          className={`text-xs px-2 py-0.5 rounded-full ${node.is_override ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
      {node.is_override ? 'Override' : 'Set here'}
    </span>
  );
}

function Node({ node, depth, admin, onSet, onTransfer }) {
  const Icon = ICONS[node.scope];
  const owner = node.originating_user;
  const ownHere = node.attribution_id && !node.inherited;
  return (
    <div data-testid={`attr-node-${node.scope_id}`}
         className={`${INDENT[depth]} flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-3 py-2 pr-2 border-b border-gray-100 dark:border-gray-800 last:border-0`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
        <Icon className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{node.label || roleLabel(node.scope)}</span>
        <Badge node={node} />
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0 pl-6 sm:pl-0">
        <span className="text-sm font-medium text-gray-900 dark:text-white" data-testid={`attr-owner-${node.scope_id}`}>
          {owner?.restricted ? <span className="inline-flex items-center gap-1 text-gray-500"><Lock className="w-3 h-3" />Restricted</span> : owner?.name || '—'}
        </span>
        {admin && !ownHere && (
          <Button size="sm" variant="outline" onClick={() => onSet(node)} data-testid={`attr-set-${node.scope_id}`}>
            {node.attribution_id ? 'Override' : 'Set'}
          </Button>
        )}
        {admin && ownHere && (
          <Button size="sm" variant="ghost" onClick={() => onTransfer(node)} data-testid={`attr-transfer-${node.scope_id}`}>
            <GitBranch className="w-3.5 h-3.5 mr-1" /> Change
          </Button>
        )}
      </div>
    </div>
  );
}

function flatten(tree) {
  const rows = [[tree, 0]];
  (tree.entities || []).forEach((e) => {
    rows.push([e, 1]);
    (e.locations || []).forEach((l) => {
      rows.push([l, 2]);
      (l.processing_accounts || []).forEach((p) => rows.push([p, 3]));
    });
  });
  return rows;
}

export function AttributionTree({ tree, admin, onSet, onTransfer }) {
  if (!tree) return null;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3" data-testid="attribution-tree">
      {flatten(tree).map(([node, depth]) => (
        <Node key={`${node.scope}-${node.scope_id}`} node={node} depth={depth} admin={admin} onSet={onSet} onTransfer={onTransfer} />
      ))}
    </div>
  );
}
